/**
 * 発話単位話者帰属 (`scenes.dialogue_lines`) の FE 表示・編集用ヘルパー。
 * docs/features/scenario-dialogue-speaker-plan.md §3.8 (フェーズ3)。
 *
 * 型・ハード制約の数値は `features/scenarios/types.ts` を正本として re-export する
 * (BE の `mention_tokens.py` と揃える。実際の enforcement は BE の 422 判定)。
 */
import {
  DIALOGUE_LINE_LABEL_MAX_CHARS,
  DIALOGUE_LINE_TEXT_MAX_CHARS,
  type DialogueLine,
  type DialogueLineActorKind,
  type DialogueLineSpeaker,
  type ScenarioCastRole,
} from '../../../features/scenarios/types';
import { mentionBadgeLabel, mentionIdHex4, type MentionActorSource, type MentionCandidate } from '../state/mentionUtils';
import {
  NARRATION_VOICE_SPEAKER_LABEL,
  ensureVoiceLineIds,
  narrationVoiceSpeaker,
  newVoiceLineId,
} from '../../../features/scenarios/scriptContract';
import type { Scenario } from './types';

export {
  DIALOGUE_LINE_LABEL_MAX_CHARS,
  DIALOGUE_LINE_TEXT_MAX_CHARS,
};
export type { DialogueLine, DialogueLineActorKind, DialogueLineSpeaker, ScenarioCastRole };

/**
 * シナリオの役柄名 (generation_metadata.cast_roles)。未設定・旧シナリオは []。
 * 俳優の表示名は利用者がキャストを選ぶためのもので、シナリオ生成以降の話者表示には
 * 役柄名 (無ければ「登場人物A」) を使う (docs/features/scenario-cast-role-names-plan.md)。
 */
export function scenarioCastRoles(
  scenario: { generation_metadata?: Record<string, unknown> | null } | null | undefined,
): ScenarioCastRole[] {
  const raw = (scenario?.generation_metadata as { cast_roles?: unknown } | null | undefined)?.cast_roles;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (c): c is ScenarioCastRole => !!c && typeof c === 'object'
      && typeof (c as ScenarioCastRole).letter === 'string' && typeof (c as ScenarioCastRole).role === 'string'
      && !!(c as ScenarioCastRole).role,
  );
}

/**
 * キャスト順 index (= 識別子 A, B, …・mentionBadgeLabel と同じ払い出し) の役柄名。無ければ null。
 * 照合は letter で行う (cast_roles.actor_id は生成時点の俳優で、入れ替え後は古くなるため)。
 */
export function castRoleForIndex(castRoles: ScenarioCastRole[], index: number): string | null {
  const letter = mentionBadgeLabel(index);
  const hit = castRoles.find((c) => c.letter.toUpperCase() === letter);
  return hit?.role || null;
}

/** キャスト順 index の話者ラベル: 役柄名、無ければ「登場人物A」。俳優の表示名は使わない。 */
export function castSpeakerLabel(castRoles: ScenarioCastRole[], index: number): string {
  return castRoleForIndex(castRoles, index) ?? `登場人物${mentionBadgeLabel(index)}`;
}

/**
 * @メンション俳優候補 (mentionActorSources) に役柄名を付ける。チップのホバー名・候補
 * ポップアップの名前が俳優の表示名ではなく役柄名になる。actors の index = キャスト順 =
 * 識別子 (buildMentionActorSources と buildDialogueSpeakerOptions は同じ順序規則)。
 */
export function applyCastRolesToMentionActorSources(
  actors: MentionActorSource[],
  scenario: { generation_metadata?: Record<string, unknown> | null } | null | undefined,
): MentionActorSource[] {
  const castRoles = scenarioCastRoles(scenario);
  if (castRoles.length === 0) return actors;
  return actors.map((actor, idx) => {
    const roleName = castRoleForIndex(castRoles, idx);
    return roleName ? { ...actor, roleName } : actor;
  });
}

/**
 * 行エディタ (DialogueLinesEditor) が扱う汎用の話者付き行。セリフ (DialogueLine) は
 * そのまま代入可能 (`special` 無し)。テロップ (telop_lines) は telopLines.ts のアダプタが
 * 「ナレーション」「話者なし」を `special` で表す (docs/features/scenario-telop-speaker-plan.md §3.7)。
 */
export type SpeakerLineSpeaker = DialogueLineSpeaker & { special?: 'narration' | 'none' };
/**
 * 行エディタの行。声の台本 (motion-v2) の任意キー (声の行の line_id / voice_source / style・
 * テロップの voice_ref) は行エディタが触らずにそのまま持ち回る (行の編集は spread で更新する)。
 */
export type SpeakerLine = {
  text: string;
  speaker: SpeakerLineSpeaker;
  line_id?: string;
  voice_source?: DialogueLine['voice_source'];
  style?: string;
  excluded?: boolean;
  voice_ref?: string[];
};

/** 話者ピッカーの追加候補 (登場人物宣言 / ナレーション / 話者なし)。value は select 値。 */
export type ExtraSpeakerOption = {
  value: string;
  label: string;
  /** チップの見た目。person (既定) = ダミー人物 / mic = ナレーション / none = 話者なし。 */
  glyph?: 'person' | 'mic' | 'none';
};

export const NARRATION_SPEAKER_VALUE = 'narration:';
export const NO_SPEAKER_VALUE = 'none:';
export function characterSpeakerValue(key: string): string {
  return `character:${key}`;
}

/**
 * DB 行 (API レスポンス) の `dialogue_lines` を UI state 用に正規化する。
 * `scene.dialogue_lines ?? null` と書くだけの薄いラッパーだが、過去に `|| ''` 的な
 * falsy 潰しで生成後リフレッシュのマッピングから値が欠落したバグ (教訓: 生成後
 * リフレッシュの FE マッピングにフィールドを追加し忘れると画面上だけ値が欠落する)
 * の再発防止のため、扱いを 1 箇所に集約してテストする。
 * `[]` (構造化モード・セリフなし) は NULL (レガシーモード) に潰してはならない。
 */
export function toUiDialogueLines(raw: DialogueLine[] | null | undefined): DialogueLine[] | null {
  return raw ?? null;
}

export function createEmptyDialogueLine(): DialogueLine {
  return { speaker: { label: '', actor_id: null, actor_kind: null }, text: '' };
}

/** 話者ピッカーの選択肢1件分 (キャスト俳優)。「自由入力」はピッカー側で別途扱う。 */
export type DialogueSpeakerOption = {
  key: string;
  label: string;
  actor_id: string;
  actor_kind: DialogueLineActorKind;
};

/**
 * シナリオのキャスト俳優から話者ピッカーの選択肢を組み立てる。
 * casting_model_ids (AI俳優・actor_kind='actor') と seedance_preset_actor_ids
 * (プリセット俳優・actor_kind='seedance_preset') の両方を候補にする
 * (docs/features/scenario-dialogue-speaker-plan.md §3.8。@メンション候補の
 * gatedMentionActorSources とは異なり、モデル種別/スタイライズ/参照上限による
 * ゲート・切り捨ては行わない — 話者帰属は @メンション挿入とは無関係な用途のため、
 * プリセット俳優キャストのシナリオでも常に選択肢に出す)。
 */
export function buildDialogueSpeakerOptions(
  scenario: Pick<Scenario, 'casting_models' | 'casting' | 'seedance_preset_actors' | 'seedance_preset_actor' | 'generation_metadata'>,
): DialogueSpeakerOption[] {
  // label は役柄名 (generation_metadata.cast_roles)、無ければ「登場人物A」。俳優の表示名は
  // キャスト選択用で、選んだ行の speaker.label には入れない (BE の話者展開と同じ規則)。
  const castRoles = scenarioCastRoles(scenario);
  const options: DialogueSpeakerOption[] = [];
  const castingList = scenario.casting_models?.length
    ? scenario.casting_models
    : (scenario.casting ? [scenario.casting] : []);
  for (const actor of castingList) {
    options.push({
      key: `actor:${actor.id}`,
      label: castSpeakerLabel(castRoles, options.length),
      actor_id: actor.id,
      actor_kind: 'actor',
    });
  }
  const presetList = scenario.seedance_preset_actors?.length
    ? scenario.seedance_preset_actors
    : (scenario.seedance_preset_actor ? [scenario.seedance_preset_actor] : []);
  for (const actor of presetList) {
    options.push({
      key: `seedance_preset:${actor.id}`,
      label: castSpeakerLabel(castRoles, options.length),
      actor_id: actor.id,
      actor_kind: 'seedance_preset',
    });
  }
  return options;
}

/**
 * 行の現在の話者が options に含まれていなければ末尾へ足す (プリセット俳優キャストの
 * シナリオでも現在の話者がピッカーに表示され、編集保存で actor_id が落ちないように。
 * §3.8 / plan レビュー3巡 指摘2)。actor_id が無い自由ラベル話者はここでは追加しない
 * (ピッカーの「自由入力」枝で表現するため)。
 */
export function ensureSpeakerOptionIncludesCurrent(
  options: DialogueSpeakerOption[],
  current: DialogueLineSpeaker,
): DialogueSpeakerOption[] {
  if (!current.actor_id || !current.actor_kind) return options;
  const exists = options.some((o) => o.actor_id === current.actor_id && o.actor_kind === current.actor_kind);
  if (exists) return options;
  return [
    ...options,
    { key: `${current.actor_kind}:${current.actor_id}`, label: current.label, actor_id: current.actor_id, actor_kind: current.actor_kind },
  ];
}

/** 話者ピッカーの select value ⇔ DialogueLineSpeaker 変換 (自由入力は '__custom__')。 */
export const CUSTOM_SPEAKER_VALUE = '__custom__';

export function speakerToSelectValue(speaker: SpeakerLineSpeaker): string {
  if (speaker.special === 'narration' || speaker.kind === 'narration') return NARRATION_SPEAKER_VALUE;
  if (speaker.special === 'none') return NO_SPEAKER_VALUE;
  if (speaker.actor_id && speaker.actor_kind) return `${speaker.actor_kind}:${speaker.actor_id}`;
  if (speaker.character_key) return characterSpeakerValue(speaker.character_key);
  return CUSTOM_SPEAKER_VALUE;
}

/**
 * update_scene (`PUT /generation/scenes/<id>`) の API 返却値から、dialogue_lines
 * 保存後にローカル state へ反映すべき値を組み立てる
 * (docs/features/scenario-dialogue-speaker-plan.md §3.6 レビューP1指摘)。
 *
 * BE は strict 正規化・actor_id の組織スコープ検証 (無効なら null 化)・
 * dialogue/script の3値同期導出を行うため、送信前の値 (`sentLines`) をそのまま
 * ローカル state に書き戻すと、これらの結果が画面に反映されない乖離が生じる。
 * 返却値の形が想定外 (sceneId 不一致・null 等) の場合は送信前の値へフォールバックし、
 * 黙って握り潰さない。
 */
/**
 * DialogueLineActorKind ('actor' | 'seedance_preset') → MentionActorSource.kind
 * ('casting' | 'seedance-preset') の呼称変換。dialogue_lines の話者と
 * @メンション俳優候補 (mentionUtils.ts) は同じ俳優を指すが命名規約が異なるため揃える。
 */
export function dialogueActorKindToMentionKind(kind: DialogueLineActorKind): MentionActorSource['kind'] {
  return kind === 'actor' ? 'casting' : 'seedance-preset';
}

/** actors (mentionActorSources と同じ配列) 内での該当俳優の index。見つからなければ -1。 */
function findMentionActorIndex(
  actorId: string,
  actorKind: DialogueLineActorKind,
  actors: MentionActorSource[],
): number {
  const mentionKind = dialogueActorKindToMentionKind(actorKind);
  return actors.findIndex((a) => a.id === actorId && a.kind === mentionKind);
}

/** セリフ行の話者チップ (極小顔写真+バッジ文字) の表示情報。 */
export type DialogueSpeakerChip = {
  /** 映像・ビジュアル欄の @メンションバッジと同じ文字 (A, B, C…)。 */
  badge: string;
  thumbnailUrl: string | null;
  name: string;
};

/**
 * 話者の話者チップ表示情報を解決する。`actors` には映像・ビジュアル欄の @メンション
 * チップと同じ配列 (SceneDetailColumn の mentionActorSources) を渡すことで、バッジ文字
 * (mentionBadgeLabel(index)) が視覚欄のチップと一致する — buildMentionCandidates が
 * 俳優→素材の順でバッジを払い出す際、俳優の index は actors 配列内の位置だけで決まり
 * 素材の有無に影響されないため (mentionUtils.ts buildMentionCandidates)。
 *
 * actor_id が無い自由ラベル話者、または actors 内に見つからない話者 (現行キャストに
 * 存在しない=stale、もしくはモデル種別/スタイライズ/参照上限で @メンション候補から
 * 外れている) は null を返し、呼び出し側は label テキスト表示へフォールバックする。
 */
export function resolveDialogueSpeakerChip(
  speaker: DialogueLineSpeaker,
  actors: MentionActorSource[],
): DialogueSpeakerChip | null {
  if (!speaker.actor_id || !speaker.actor_kind) return null;
  const idx = findMentionActorIndex(speaker.actor_id, speaker.actor_kind, actors);
  if (idx === -1) return null;
  const actor = actors[idx];
  // ホバーに出す名前は行の話者ラベル (役柄名)。俳優の表示名はラベルが空のときの最後の手段。
  return {
    badge: mentionBadgeLabel(idx),
    thumbnailUrl: actor.imageUrl ?? null,
    name: speaker.label || actor.roleName || actor.displayName,
  };
}

/**
 * 話者ピッカーの option 1件分のバッジ文字 (見つからなければ null)。
 * DialogueLinesEditor の option 文言に「A: 佐藤ゆい」のようにバッジを前置するために使う。
 */
export function resolveDialogueSpeakerOptionBadge(
  option: Pick<DialogueSpeakerOption, 'actor_id' | 'actor_kind'>,
  actors: MentionActorSource[],
): string | null {
  const idx = findMentionActorIndex(option.actor_id, option.actor_kind, actors);
  return idx === -1 ? null : mentionBadgeLabel(idx);
}

/** 話者ピッカー「自由入力」擬似候補の表示名。 */
export const CUSTOM_SPEAKER_LABEL = '自由入力';

/**
 * 話者ピッカー (映像・ビジュアル欄の @メンション候補と同じ見た目のドロップダウン。
 * MaterialMentionPopup を流用) に渡す候補配列を組み立てる。
 * `options` (ensureSpeakerOptionIncludesCurrent 適用後) の各行を MentionCandidate 形状へ
 * 変換し、末尾に「自由入力」の擬似候補を足す。
 *
 * `target.id` は素材/俳優の実 ID そのものではなく `speakerToSelectValue` と同じ
 * `${actor_kind}:${actor_id}` / `CUSTOM_SPEAKER_VALUE` 文字列にする (この用途内でのみ
 * 選択の逆引きキーとして使う。@メンショントークンの4hex解決とは無関係)。
 * バッジ文字は actors (mentionActorSources) 内に見つかった場合のみ付く
 * (resolveDialogueSpeakerOptionBadge と同じ判定)。見つからない (stale / ゲート除外) 場合は
 * 空文字のまま名前だけを表示する。
 */
export function buildDialogueSpeakerCandidates(
  options: DialogueSpeakerOption[],
  actors: MentionActorSource[],
  extraOptions: ExtraSpeakerOption[] = [],
): MentionCandidate[] {
  const candidates: MentionCandidate[] = options.map((opt) => {
    const idx = findMentionActorIndex(opt.actor_id, opt.actor_kind, actors);
    const actor = idx === -1 ? null : actors[idx];
    return {
      kind: 'actor',
      target: {
        id: `${opt.actor_kind}:${opt.actor_id}`,
        thumbnailUrl: actor?.imageUrl ?? null,
        name: opt.label,
      },
      badge: idx === -1 ? '' : mentionBadgeLabel(idx),
      idHex4: mentionIdHex4(opt.actor_id),
    };
  });
  // 追加候補 (登場人物宣言 / ナレーション / 話者なし) はキャスト俳優の後・「自由入力」の前。
  for (const extra of extraOptions) {
    candidates.push({
      kind: 'asset',
      target: { id: extra.value, thumbnailUrl: null, name: extra.label },
      badge: '',
      idHex4: '',
    });
  }
  candidates.push({
    kind: 'asset',
    target: { id: CUSTOM_SPEAKER_VALUE, thumbnailUrl: null, name: CUSTOM_SPEAKER_LABEL },
    badge: '',
    idHex4: '',
  });
  return candidates;
}

/**
 * 話者ピッカーで候補 (buildDialogueSpeakerCandidates が返した1件の target.id) を
 * 選んだときの DialogueLineSpeaker への反映内容を求める。DialogueLinesEditor の
 * 旧 `<select onChange>` 分岐 (§3.8) をポップアップ選択向けに切り出したもの。
 * `currentLabel` は自由入力への切り替え時 (actor_id/actor_kind を null に戻すだけで
 * ラベルは維持する) にそのまま使う。
 */
export function resolveDialogueSpeakerSelection(
  selectedValue: string,
  currentLabel: string,
  options: DialogueSpeakerOption[],
  extraOptions: ExtraSpeakerOption[] = [],
): Partial<SpeakerLineSpeaker> {
  // kind ('narration') はナレーター以外を選んだら必ず外す (声の台本で自由ラベルに kind だけ残ると BE がナレーター扱いにする)
  if (selectedValue === CUSTOM_SPEAKER_VALUE) {
    return { kind: undefined, actor_id: null, actor_kind: null, character_key: undefined, special: undefined };
  }
  if (selectedValue === NARRATION_SPEAKER_VALUE) {
    // テロップは「ナレーション」・声の台本の声の行は「ナレーター」(追加候補のラベルに従う)
    const extra = extraOptions.find((o) => o.value === NARRATION_SPEAKER_VALUE);
    return { kind: 'narration', actor_id: null, actor_kind: null, character_key: undefined, special: 'narration', label: extra?.label ?? 'ナレーション' };
  }
  if (selectedValue === NO_SPEAKER_VALUE) {
    return { kind: undefined, actor_id: null, actor_kind: null, character_key: undefined, special: 'none', label: '' };
  }
  if (selectedValue.startsWith('character:')) {
    const key = selectedValue.slice('character:'.length);
    const extra = extraOptions.find((o) => o.value === selectedValue);
    return { kind: undefined, actor_id: null, actor_kind: null, character_key: key, special: undefined, label: extra?.label ?? currentLabel };
  }
  const [kind, id] = selectedValue.split(':') as [DialogueLineActorKind, string];
  const opt = options.find((o) => o.actor_id === id && o.actor_kind === kind);
  return { kind: undefined, actor_id: id, actor_kind: kind, character_key: undefined, special: undefined, label: opt?.label ?? currentLabel };
}

function sameStringList(a: readonly string[] | undefined, b: readonly string[] | undefined): boolean {
  const x = a ?? [];
  const y = b ?? [];
  return x.length === y.length && x.every((v, i) => v === y[i]);
}

/**
 * 2つの dialogue_lines 配列が (話者・テキストとも) 同一内容かを判定する。
 * 外側クリックの破棄確認 (2026-08-17 要件2) の dirty 判定に使う。JSON.stringify による
 * 比較はキー順序に依存し脆いため、フィールド単位で明示的に比較する。
 */
export function areDialogueLinesEqual(a: SpeakerLine[], b: SpeakerLine[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  return a.every((line, i) => {
    const other = b[i];
    return (
      !!other
      && line.text === other.text
      && line.speaker.label === other.speaker.label
      && line.speaker.actor_id === other.speaker.actor_id
      && line.speaker.actor_kind === other.speaker.actor_kind
      && (line.speaker.character_key ?? null) === (other.speaker.character_key ?? null)
      && (line.speaker.special ?? null) === (other.speaker.special ?? null)
      && (line.speaker.kind ?? null) === (other.speaker.kind ?? null)
      && sameStringList(line.voice_ref, other.voice_ref)
    );
  });
}

/**
 * 構造化セリフ (dialogue_lines) 編集中に外側をクリックしたとき、編集内容を破棄して
 * 閉じてよいかを判定する。編集開始時の値 (`original`) から変更が無ければ確認なしで
 * true (無言で閉じる・従来どおり)。変更があれば `confirmFn` (通常 window.confirm) の
 * 結果を返す — false ならキャンセル (編集継続・呼び出し側は閉じない)。
 * docs 上のUI確認手段の選定理由: このサイトは discard/delete 系の軽い確認に
 * window.confirm を多用しており (BrandCutSection 等)、ここは document 直下の
 * mousedown ハンドラ (useSceneActions) という React ツリー外の文脈で同期的に
 * 結果が要る箇所のため、モーダル state を新設せずこれに合わせる (2026-08-17 要件2)。
 */
export function shouldDiscardDialogueLinesEdit(
  current: DialogueLine[],
  original: DialogueLine[],
  confirmFn: (message: string) => boolean,
): boolean {
  if (areDialogueLinesEqual(current, original)) return true;
  return confirmFn('編集中のセリフを破棄しますか？');
}

export function resolveDialogueLinesSaveResult(
  sceneId: string,
  sentLines: DialogueLine[],
  response: { id?: string; dialogue_lines?: DialogueLine[] | null; dialogue?: string; script?: string } | null | undefined,
): { dialogue_lines: DialogueLine[]; dialogue?: string; script?: string } {
  const isExpectedShape = !!response && typeof response === 'object' && response.id === sceneId;
  if (!isExpectedShape) {
    return { dialogue_lines: sentLines };
  }
  return {
    dialogue_lines: response!.dialogue_lines ?? sentLines,
    dialogue: response!.dialogue,
    script: response!.script,
  };
}

// ── 声の台本 (script_contract='motion-v2'・docs/features/brand-cut-motion-graphics-plan.md §10.7.2) ──

/** 声の台本の声の行エディタの追加候補: ナレーター (予約語 narration) → 登場人物宣言。 */
export function buildVoiceSpeakerExtraOptions(characters: Array<{ key: string; label: string }>): ExtraSpeakerOption[] {
  return [
    { value: NARRATION_SPEAKER_VALUE, label: NARRATION_VOICE_SPEAKER_LABEL, glyph: 'mic' },
    ...characters.map((c) => ({ value: characterSpeakerValue(c.key), label: c.label, glyph: 'person' as const })),
  ];
}

/** 声の台本の「+ 行を追加」の初期値: ナレーター行 (line_id は先に付けて、テロップの「合わせる声」から指せるようにする)。 */
export function createEmptyVoiceLine(existing: ReadonlyArray<Pick<DialogueLine, 'line_id'>> = []): DialogueLine {
  return {
    line_id: newVoiceLineId(existing.map((l) => l.line_id).filter((id): id is string => !!id)),
    speaker: narrationVoiceSpeaker(),
    text: '',
  };
}

/**
 * 声の行を API へ送る形にする: ナレーター (kind / special が narration) は `{kind:'narration', label:'ナレーター'}` に揃え、
 * 行エディタの表示用の special を落とし、line_id の無い行に id を付ける (既存の id・voice_source・style は保つ)。
 */
export function toVoiceScriptDialogueLines(lines: ReadonlyArray<SpeakerLine | DialogueLine>): DialogueLine[] {
  const normalized = lines.map((line): DialogueLine => {
    const sp = line.speaker as SpeakerLineSpeaker;
    const isNarration = sp.kind === 'narration' || sp.special === 'narration';
    const { special: _special, kind: _kind, ...rest } = sp;
    void _special;
    void _kind;
    const speaker: DialogueLineSpeaker = isNarration ? narrationVoiceSpeaker() : rest;
    const out: DialogueLine = { speaker, text: line.text };
    if (line.line_id) out.line_id = line.line_id;
    if (line.voice_source) out.voice_source = line.voice_source;
    if (line.style) out.style = line.style;
    if (line.excluded) out.excluded = true;
    return out;
  });
  return ensureVoiceLineIds(normalized);
}

/**
 * 改行を含む 1 行の本文を、行数の中央で 2 つに分ける (2 行 → 1+1・3 行 → 2+1・4 行 → 2+2)。
 * 改行が無ければ null。
 */
export function splitLineTextInHalf(text: string): [string, string] | null {
  const parts = text.split('\n');
  if (parts.length < 2) return null;
  const head = Math.ceil(parts.length / 2);
  return [parts.slice(0, head).join('\n'), parts.slice(head).join('\n')];
}
